import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaBars, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

const MOBILE_BREAKPOINT = 768; // Match this with theme.breakpoints.mobile

const HeaderContainer = styled.header`
  background-color: ${({ isScrolled, theme }) => 
    isScrolled ? '#ffffff' : 'rgba(255, 255, 255, 0.95)'};
  padding: ${({ theme }) => theme.spacing.medium};
  box-shadow: ${({ isScrolled }) => 
    isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease-in-out;
  backdrop-filter: blur(10px);
  border-bottom: ${({ isScrolled, theme }) => 
    isScrolled ? `1px solid ${theme.colors.border}` : 'none'};
  border-radius: 8px;
  margin: 10px;
  z-index: 1000;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${({ theme }) => theme.spacing.small};
    margin: 0;
    border-radius: 0;
  }
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.medium};
  height: 70px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1001;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 ${({ theme }) => theme.spacing.small};
    height: 60px;
    border-radius: 0;
  }
`;

const Logo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  z-index: 1002;
  transition: color 0.3s ease;
  
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1.2rem;
  }
`;

const MobileMenuOverlay = styled.div`
  display: none;
  
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99999; /* Set very high */
    backdrop-filter: blur(0px);
  }
`;


const NavLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  align-items: center;
  transition: all 0.3s ease-in-out;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.colors.background};
    padding: 80px 20px;
    z-index: 1001;
    gap: 20px;
    overflow-y: auto;
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
    box-shadow: ${({ isOpen }) => 
      isOpen ? '-5px 0 15px rgba(0, 0, 0, 0.1)' : 'none'};
    border-radius: 0;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  transition: all 0.2s;
  font-size: 1.1rem;
  position: relative;
  padding: 8px 12px;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 50%;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    &:after {
      width: 80%;
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1.3rem;
    padding: 15px;
    width: 100%;
    text-align: center;
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.background};
    border-radius: 8px;
    margin: 5px 0;
    
    &:hover {
      background-color: ${({ theme }) => `${theme.colors.primary}10`};
    }
    
    &:after {
      display: none;
    }
  }
`;

const AuthContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    width: 100%;
    padding: 20px 0;
  }
`;

const UserMenu = styled.div`
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const UserIcon = styled(FaUser)`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ShoppingBag = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(0.8);
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;

  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    padding: 0.8rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1002;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}10`};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setIsMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobileView = width <= MOBILE_BREAKPOINT;
      setIsMobile(isMobileView);
      
      if (!isMobileView && isMenuOpen) {
        closeMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !menuButtonRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <>
      <HeaderContainer isScrolled={isScrolled}>
        <Nav>
          <Logo to="/" onClick={closeMenu}>
            Shubhangee Creations
          </Logo>
          
          <MenuButton 
            ref={menuButtonRef} 
            onClick={toggleMenu}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </MenuButton>

          <MobileMenuOverlay isOpen={isMenuOpen} onClick={closeMenu} />
          
          <NavLinks ref={menuRef} isOpen={isMenuOpen}>
            <NavLink to="/" onClick={closeMenu}>
              Home
            </NavLink>
            <NavLink to="/categories" onClick={closeMenu}>
              Categories
            </NavLink>
            <NavLink to="/about" onClick={closeMenu}>
              About
            </NavLink>
            <NavLink to="/contact" onClick={closeMenu}>
              Contact
            </NavLink>
            
            <AuthContainer>
              {user ? (
                <>
                  <UserMenu>
                    <UserIcon />
                  </UserMenu>
                  <Button onClick={handleLogout}>Logout</Button>
                </>
              ) : (
                <>
                  <NavLink to="/login" onClick={closeMenu}>
                    Login
                  </NavLink>
                  <NavLink to="/signup" onClick={closeMenu}>
                    Sign Up
                  </NavLink>
                </>
              )}
              <NavLink to="/cart" onClick={closeMenu}>
                <ShoppingBag src="/shopping-bag.png" alt="Shopping Bag" />
              </NavLink>
            </AuthContainer>
          </NavLinks>
        </Nav>
      </HeaderContainer>
    </>
  );
};

export default Header;